<template>
  <section class="success-stories-section">
    <div class="container">
      <div class="section-title text-center">
        <span class="subtitle">Erfolgsgeschichten</span>
        <h2>Unsere <span class="text-gradient">Projekte</span> im Überblick</h2>
        <p class="lead">Entdecken Sie, wie unsere KI- und DevOps-Lösungen Unternehmen transformieren</p>
      </div>
      
      <div class="projects-container mt-5">
        <div v-for="project in displayedProjects" 
             :key="project.id" 
             class="case-study-card">
          <div class="case-header">
            <h3>{{ project.title }}</h3>
            <span class="industry-badge">{{ project.industry }}</span>
          </div>
          <div class="case-content">
            <p>{{ project.description }}</p>
            <ul class="case-results">
              <li v-for="(result, idx) in project.results" :key="idx">{{ result }}</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div class="text-center mt-5" v-if="projects.length > initialProjectCount && !showAllProjects">
        <button @click="loadMoreProjects" class="btn-load-more">
          Weitere Projekte anzeigen
          <i class="fas fa-arrow-right ml-2"></i>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'SuccessStories',
  data() {
    return {
      initialProjectCount: 6,
      showAllProjects: false,
      projects: [
        {
          id: 1,
          title: "KI-gestützte Qualitätskontrolle",
          industry: "Fertigung",
          description: "Ein führender Automobilzulieferer implementierte unsere Computer-Vision-Lösung zur automatisierten Qualitätskontrolle von Bauteilen.",
          results: [
            "90% Reduktion manueller Inspektionen",
            "Fehlerrate um 85% reduziert",
            "ROI innerhalb von 8 Monaten"
          ],
          category: "ki"
        },
        {
          id: 2,
          title: "CI/CD-Pipeline-Transformation",
          industry: "FinTech",
          description: "Eine Finanzplattform optimierte ihre Entwicklungsprozesse durch eine vollständige DevOps-Transformation und Automatisierung.",
          results: [
            "Release-Zyklen von Wochen auf Stunden verkürzt",
            "98% weniger manuelle Deployments",
            "Entwicklerproduktivität um 40% gesteigert"
          ],
          category: "devops"
        },
        {
          id: 3,
          title: "KI-gestützte DevOps-Optimierung",
          industry: "E-Commerce",
          description: "Ein E-Commerce-Unternehmen implementierte eine KI-basierte Lösung zur Vorhersage von Infrastruktur-Bedarf und automatisierte Skalierung.",
          results: [
            "Infrastrukturkosten um 35% reduziert",
            "Zero-Downtime bei Verkaufsspitzen",
            "Kundenbesuche um 28% gestiegen"
          ],
          category: "hybrid"
        },
        {
          id: 4,
          title: "KI für Betrugserkennung",
          industry: "Finanzsektor",
          description: "Eine Bank implementierte unser KI-gestütztes Betrugserkennungssystem zur Echtzeitanalyse von Transaktionen.",
          results: [
            "Betrugsfälle um 92% schneller erkannt",
            "Falsch-Positive-Rate um 76% reduziert",
            "Jährliche Einsparungen: 1,2 Mio. Euro"
          ],
          category: "ki"
        },
        {
          id: 5,
          title: "Microservices-Migration",
          industry: "Telekommunikation",
          description: "Ein Telekommunikationsanbieter migrierte von einem Monolithen zu einer Microservices-Architektur mit unserem DevOps-Framework.",
          results: [
            "Systemverfügbarkeit auf 99,99% erhöht",
            "Feature-Releases 20x schneller",
            "API-Antwortzeiten um 65% verbessert"
          ],
          category: "devops"
        },
        {
          id: 6,
          title: "Predictive Maintenance",
          industry: "Energiesektor",
          description: "Ein Energieversorger nutzt unsere KI-gestützte Predictive-Maintenance-Lösung für Windkraftanlagen mit integriertem DevOps-Monitoring.",
          results: [
            "Ungeplante Ausfallzeiten um 78% reduziert",
            "Wartungskosten um 42% gesenkt",
            "Energieeffizienz um 15% gesteigert"
          ],
          category: "hybrid"
        },
        {
          id: 7,
          title: "Automatisierte Datenanalytik",
          industry: "Gesundheitswesen",
          description: "Ein Gesundheitsdienstleister nutzt unsere KI-Lösung zur automatisierten Analyse von Patientendaten und klinischen Studien.",
          results: [
            "Datenauswertung 60% schneller",
            "Mustererkennung verbessert um 75%",
            "Patientenversorgung personalisiert"
          ],
          category: "ki"
        },
        {
          id: 8,
          title: "Cloud-Migration & Optimierung",
          industry: "Medien",
          description: "Ein Medienunternehmen optimierte seine Cloud-Infrastruktur durch unsere DevOps-Expertise und automatisierte Skalierung.",
          results: [
            "Cloud-Kosten um 45% reduziert",
            "Content-Delivery 3x schneller",
            "Entwicklungszyklen halbiert"
          ],
          category: "devops"
        },
        {
          id: 9,
          title: "KI-gestützte Kundensegmentierung",
          industry: "Einzelhandel",
          description: "Ein Einzelhändler implementierte unsere AI-Lösung zur Kundensegmentierung und personalisierten Angeboten.",
          results: [
            "Conversion-Rate um 65% erhöht",
            "Customer Lifetime Value +40%",
            "Marketing-ROI verdreifacht"
          ],
          category: "ki"
        }
      ]
    };
  },
  computed: {
    displayedProjects() {
      return this.showAllProjects 
        ? this.projects 
        : this.projects.slice(0, this.initialProjectCount);
    }
  },
  methods: {
    loadMoreProjects() {
      this.showAllProjects = true;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/global.scss";

.success-stories-section {
  padding: 100px 0;
  background: linear-gradient(0deg, rgba($accent-color, 0.03), rgba($primary-color, 0.2));
  position: relative;
}

.projects-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
}

.case-study-card {
  background: rgba($secondary-color, 0.3);
  border-radius: 15px;
  overflow: hidden;
  height: 100%;
  transition: all 0.3s ease;
  border: 1px solid rgba($accent-color, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
  
  .case-header {
    background: $accent-gradient;
    padding: 20px;
    position: relative;
    
    h3 {
      color: white;
      margin-bottom: 10px;
      font-size: 1.4rem;
    }
    
    .industry-badge {
      display: inline-block;
      background: rgba(white, 0.2);
      padding: 5px 10px;
      font-size: 0.8rem;
      border-radius: 20px;
      color: white;
    }
  }
  
  .case-content {
    padding: 20px;
    
    p {
      color: $muted-text-color;
      margin-bottom: 20px;
    }
    
    .case-results {
      list-style: none;
      padding-left: 0;
      
      li {
        padding: 8px 0;
        position: relative;
        padding-left: 25px;
        color: $text-color;
        
        &::before {
          content: '→';
          position: absolute;
          left: 0;
          color: $accent-color;
        }
      }
    }
  }
}

.btn-load-more {
  background: $accent-gradient;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 30px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba($accent-color, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba($accent-color, 0.4);
  }
  
  i {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover i {
    transform: translateX(5px);
  }
}

/* Responsive styles */
@media (max-width: 992px) {
  .projects-container {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .projects-container {
    grid-template-columns: 1fr;
  }
}
</style>
